import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute, RequirePermission } from "@/components/ProtectedRoute";
import { PageLoader } from "@/components/admin/LoadState";

// El editor de plantillas trae el lienzo de React Email (~700 kB): solo se descarga al abrirlo.
const NotificationTemplateEditor = lazy(() => import("@/pages/admin/NotificationTemplateEditor"));

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Programa from "@/pages/Programa";

import CustomerLayout from "@/pages/customer/CustomerLayout";
import Dashboard from "@/pages/customer/Dashboard";
import Catalog from "@/pages/customer/Catalog";
import Notifications from "@/pages/customer/Notifications";
import Beneficios from "@/pages/customer/Beneficios";
import Planes from "@/pages/customer/Planes";
import ContratarPlan from "@/pages/customer/ContratarPlan";
import Soporte from "@/pages/customer/Soporte";
import PaymentSuccess from "@/pages/customer/PaymentSuccess";
import PaymentCancel from "@/pages/customer/PaymentCancel";
import PagoSimulado from "@/pages/customer/PagoSimulado";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminRedemptions from "@/pages/admin/AdminRedemptions";
import AdminRewards from "@/pages/admin/AdminRewards";
import AdminRules from "@/pages/admin/AdminRules";
import AdminAutoRules, { AutoGrantsHistoryPage } from "@/pages/admin/AdminAutoRules";
import AdminBadges from "@/pages/admin/AdminBadges";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminEmails from "@/pages/admin/AdminEmails";
import AdminOdoo from "@/pages/admin/AdminOdoo";
import MisFacturas from "@/pages/customer/MisFacturas";
import AdminNotificationTemplates from "@/pages/admin/AdminNotificationTemplates";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminHistory from "@/pages/admin/AdminHistory";
import AdminDocuments from "@/pages/admin/AdminDocuments";
import AdminFeatures from "@/pages/admin/AdminFeatures";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import { PointsGrantPage, PointsExcelPage, PointsHistoryPage } from "@/pages/admin/AdminPoints";
import { ClientsHub, PointsHub, NotificationsHub } from "@/pages/admin/AdminHubs";
import AdminTiers from "@/pages/admin/AdminTiers";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/registro" element={<Register />} />
          <Route path="/programa" element={<Programa />} />

          <Route
            path="/payment/success"
            element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pago-simulado"
            element={
              <ProtectedRoute>
                <PagoSimulado />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment/cancel"
            element={
              <ProtectedRoute>
                <PaymentCancel />
              </ProtectedRoute>
            }
          />

          <Route
            path="/portal"
            element={
              <ProtectedRoute>
                <CustomerLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="catalogo" element={<Catalog />} />
            <Route path="canjes" element={<Navigate to="/portal/catalogo" replace />} />
            <Route path="beneficios" element={<Beneficios />} />
            <Route path="planes" element={<Planes />} />
            <Route path="planes/contratar" element={<ContratarPlan />} />
            <Route path="facturas" element={<MisFacturas />} />
            <Route path="notificaciones" element={<Notifications />} />
            <Route path="soporte" element={<Soporte />} />
          </Route>

          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<RequirePermission perm="dashboard"><AdminDashboard /></RequirePermission>} />
            {/* Gestión de clientes: lista, carga de documentos e historial en pestañas */}
            <Route path="usuarios" element={<ClientsHub />}>
              <Route index element={<RequirePermission perm="users"><AdminUsers /></RequirePermission>} />
              <Route path="cedulas" element={<RequirePermission perm="users"><AdminDocuments /></RequirePermission>} />
              <Route path="historial" element={<RequirePermission perm="users"><AdminHistory /></RequirePermission>} />
            </Route>
            <Route path="usuarios/:id" element={<RequirePermission perm="users"><AdminUserDetail /></RequirePermission>} />
            {/* Puntos: entrega (a un cliente, masiva, historial) y asignación (reglas) */}
            <Route path="puntos" element={<PointsHub />}>
              <Route index element={<RequirePermission perm="users"><PointsGrantPage /></RequirePermission>} />
              <Route path="carga-masiva" element={<RequirePermission perm="users"><PointsExcelPage /></RequirePermission>} />
              <Route path="historial" element={<RequirePermission perm="users"><PointsHistoryPage /></RequirePermission>} />
              <Route path="reglas" element={<RequirePermission perm="rules"><AdminRules /></RequirePermission>} />
              <Route path="automaticas" element={<RequirePermission perm="rules"><AdminAutoRules /></RequirePermission>} />
              <Route path="automaticas/historial" element={<RequirePermission perm="users"><AutoGrantsHistoryPage /></RequirePermission>} />
              <Route path="insignias" element={<RequirePermission perm="rules"><AdminBadges /></RequirePermission>} />
            </Route>
            {/* Direcciones anteriores: se conservan para enlaces y favoritos */}
            <Route path="documentos" element={<Navigate to="/admin/usuarios/cedulas" replace />} />
            <Route path="historial" element={<Navigate to="/admin/usuarios/historial" replace />} />
            <Route path="reglas" element={<Navigate to="/admin/puntos/reglas" replace />} />
            <Route path="canjes" element={<RequirePermission perm="redemptions"><AdminRedemptions /></RequirePermission>} />
            <Route path="suscripciones" element={<RequirePermission perm="subscriptions"><AdminSubscriptions /></RequirePermission>} />
            <Route path="catalogo" element={<RequirePermission perm="catalog"><AdminRewards /></RequirePermission>} />
            <Route path="niveles" element={<RequirePermission perm="rules"><AdminTiers /></RequirePermission>} />
            <Route path="funcionalidades" element={<RequirePermission perm="portal"><AdminFeatures /></RequirePermission>} />
            <Route path="odoo" element={<RequirePermission perm="users"><AdminOdoo /></RequirePermission>} />
            <Route path="campanas" element={<RequirePermission perm="campaigns"><AdminCampaigns /></RequirePermission>} />
            {/* Notificaciones automáticas: plantillas por evento y registro de correos */}
            <Route path="notificaciones" element={<NotificationsHub />}>
              <Route index element={<RequirePermission perm="campaigns"><AdminNotificationTemplates /></RequirePermission>} />
              <Route path="correos" element={<RequirePermission perm="campaigns"><AdminEmails /></RequirePermission>} />
            </Route>
            <Route path="notificaciones/editor/:key" element={<RequirePermission perm="campaigns"><Suspense fallback={<PageLoader />}><NotificationTemplateEditor /></Suspense></RequirePermission>} />
            <Route path="correos" element={<Navigate to="/admin/notificaciones/correos" replace />} />
            <Route path="equipo" element={<RequirePermission perm="team"><AdminTeam /></RequirePermission>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" theme="light" richColors />
    </AuthProvider>
  );
}

export default App;
